package org.evoleq.optics.storage

import io.ktor.http.*
import kotlinx.serialization.KSerializer
import org.evoleq.ktorx.result.Result
import org.evoleq.ktorx.result.ResultSerializer
import org.evoleq.ktorx.result.Serializer
import org.evoleq.math.*
import org.evoleq.optics.lens.Lens
import kotlin.reflect.KClass

/**
 * The action takes some input, which can be read from the application data with a reader [Reader<App, S>].
 * And it produces some output, which can be written to the application storage [Writer<App, T>]
 *
 * Action is functorial in S and T,
 * covariant in S and contravariant in T.
 * This turns Action into a Profunctor!
 */
data class Action<Base: Any, out I : Any, O : Any>(

    val name: String,
    val reader: Reader<Base, I>,
    // key of the needed endpoint EndPoint<I,O>
    val endPoint: KClass<*>,
    val writer: Writer<Base, O>,

    val serializer: KSerializer<@UnsafeVariance I>,
    val deserializer: KSerializer<Result<O>>,
    val onError: Writer<Base, Pair<HttpStatusCode, String>>? = null,
    val failOnError: Boolean = onError == null,
    val onSuccess: Writer<Base, O>? = null
)


/**
 * Constructor function for Actions
 */
inline fun <Base: Any, reified I : Any, reified O : Any> Action(
    name: String,
    noinline reader: Reader<Base, I>,
    // key of the needed endpoint EndPoint<I,O>
    endPoint: KClass<*>,
    noinline writer: Writer<Base, O>,
    noinline onError: Writer<Base, Pair<HttpStatusCode, String>>? = null,
    failOnError: Boolean = onError == null,
): Action<Base, I, O> = Action(
    name,
    reader,
    endPoint,
    writer ,
    Serializer<I>(),
    ResultSerializer<O>(),
    onError,
    failOnError
)


/**
 * Constructor function for Actions
 *
inline fun <Base: Any, reified I : Any, reified O : Any> Action(
    name: String,
    noinline reader: Reader<in Base, out I>,
    // key of the needed endpoint EndPoint<I,O>
    endPoint: KClass<*>,
    noinline writer: Writer<Base, List<O>>,
): Action<Base, I, List<O>> = Action(
    name,
    reader,
    endPoint,
    writer ,
    Serializer<I>(),
    ResultListSerializer<O>()
)
*/

/**
 * Functoriality of Action
 */
inline fun <Base: Any, I : Any, reified J: Any,  reified O : Any> Action<Base, I, O>.map(noinline f: (I)->J): Action<Base, J, O> = Action(
    name,
    reader map f,
    J::class,
    writer,
    Serializer<J>(),
    ResultSerializer<O>(),
    onError,
    failOnError
)

/**
 * Contra-Functoriality of Action
 */
inline fun <Base: Any, reified I : Any,  reified O : Any, reified P : Any> Action<Base, I, O>.contraMap(noinline f: (P)->O): Action<Base, I, P> = Action(
    name,
    reader,
    endPoint,
    writer contraMap f,
    Serializer<I>(),
    ResultSerializer<P>(),
    onError,
    failOnError
)


/**
 * Actions are kind of a left module w.r.t multiplication by lenses
 */
operator fun <Whole: Any, Part: Any, I : Any, O :Any>  Lens<Whole, Part>.times(action: Action<Part, I,O>): Action<Whole, I,O> = Action(
    reader = action.reader o get,
    name = action.name,
    endPoint = action.endPoint,
    writer = {o: O -> {w: Whole -> set(action.writer(o)(get(w)))(w)}},
    serializer = action.serializer,
    deserializer = action.deserializer,
    onError = when(action.onError) {
        null -> null
        else -> {o: Pair<HttpStatusCode, String> ->{w: Whole -> set(action.onError(o)(get(w)))(w)}}
    },
    failOnError = action.failOnError
)

interface ActionType

data class Actions(
    private val dispatch: HashMap<ActionType, ()->Unit>
): Map<ActionType, ()->Unit> by dispatch

fun Actions(vararg actions: Pair<ActionType,()->Unit>): Actions = Actions(hashMapOf(*actions))

fun Actions.dispatch(type: ActionType): Unit = get(type)!!()

fun String.suffixed(suffix: String?): String = when{
    suffix.isNullOrBlank() -> this
    else -> "$this:$suffix"
}
